"use client";

import React, { useState, useRef, useEffect } from "react";
import {
  motion,
  useScroll,
  useMotionValue,
  useMotionValueEvent,
  animate,
} from "motion/react";

const FeatureHighlightMotion = () => {
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [velocity, setVelocity] = useState(0);
  const sliderRef = useRef(null);
  const animationRef = useRef(null);
  const lastTimeRef = useRef(Date.now());
  const lastXRef = useRef(0);

  // Track horizontal scroll progress with Framer Motion
  const { scrollXProgress } = useScroll({ container: sliderRef });
  const maskImage = useScrollOverflowMask(scrollXProgress);

  // Card data
  const cards = [
    {
      title: "Multi-ASIN Revenue Engine",
      description:
        "Build a diverse, performing products that consistently deliver massive returns",
    },
    {
      title: "Hands-Free Growth System",
      description:
        "Create systems that generate revenue while you focus on strategy and growth",
    },
    {
      title: "Niche Blueprint",
      description: "Become the undisputed market leader in your category",
    },
    {
      title: "Self-Scaling Sales",
      description:
        "Develop systems and processes that ensure consistent growth",
    },
    {
      title: "Category Leadership Insights",
      description:
        "Leverage analytics and insights to optimize every aspect of your business",
    },
  ];

  // Momentum scroll effect
  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    const animateScroll = () => {
      if (!isDragging && Math.abs(velocity) > 0.1) {
        slider.scrollLeft += velocity;
        setVelocity(velocity * 0.95); // Apply friction
        animationRef.current = requestAnimationFrame(animateScroll);
      }
    };

    if (!isDragging) {
      animationRef.current = requestAnimationFrame(animateScroll);
    }

    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isDragging, velocity]);

  // Mouse + Touch Handlers
  const startDrag = (xPosition) => {
    setIsDragging(true);
    setStartX(xPosition - sliderRef.current.offsetLeft);
    setScrollLeft(sliderRef.current.scrollLeft);
    setVelocity(0);
    lastXRef.current = xPosition;
    lastTimeRef.current = Date.now();
    sliderRef.current.style.userSelect = "none";
  };

  const dragMove = (xPosition) => {
    if (!isDragging) return;
    const x = xPosition - sliderRef.current.offsetLeft;
    const walk = (x - startX) * 1.5;
    sliderRef.current.scrollLeft = scrollLeft - walk;

    const now = Date.now();
    const dt = now - lastTimeRef.current;
    const dx = xPosition - lastXRef.current;

    if (dt > 0) {
      setVelocity(-(dx / dt) * 10);
    }

    lastXRef.current = xPosition;
    lastTimeRef.current = now;
  };

  const stopDrag = () => {
    setIsDragging(false);
    sliderRef.current.style.userSelect = "auto";
  };

  // Handlers
  const handleMouseDown = (e) => startDrag(e.pageX);
  const handleMouseMove = (e) => dragMove(e.pageX);
  const handleTouchStart = (e) => startDrag(e.touches[0].pageX);
  const handleTouchMove = (e) => dragMove(e.touches[0].pageX);
  const handleMouseUp = stopDrag;
  const handleMouseLeave = stopDrag;
  const handleTouchEnd = stopDrag;

  return (
    <>
      <style>{`
        /* ========= Base Layout ========= */
        .feature-main-container {
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }

        .feature-main-container .slider-section {
          width: 100%;
          position: relative;
          max-width: 100%;
        }


        /* ========= Scroll Mask & Scrollbar ========= */
        .cards-wrapper {
          overflow-x: scroll;
          padding: 20px 0;
          mask-repeat: no-repeat;
          mask-size: cover;
        }

        .cards-wrapper::-webkit-scrollbar {
          height: 5px;
          background: #fff3;
          border-radius: 1ex;
          display: none;
        }

        .cards-wrapper::-webkit-scrollbar-thumb {
          background: var(--accent, #8b5cf6);
          border-radius: 1ex;
          display: none;
        }

        /* ========= Cards ========= */
        .cards-track {
          display: flex;
          gap: 20px;
          transition: transform 0.5s cubic-bezier(0.4, 0, 0.2, 1);
          padding: 0 130px;
          cursor: pointer;
        }

        .feature-main-container .card {
        width: 455px;
        height: auto;
        flex-shrink: 0;

        background: linear-gradient(
            127.75deg,
            rgba(203, 199, 252, 0.3) 13.41%,
            rgba(145, 144, 189, 0.210235) 59.59%,
            rgba(8, 16, 40, 0) 102.1%
        );
        backdrop-filter: blur(12px);

        /* Use a single border and gradient border image */
        border: 1px solid rgba(167, 139, 250, 0.5);

        border-radius: 24px;
        padding: 40px 32px;
        transition: all 0.3s ease;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        color: white;
        }

        .feature-main-container .card h3 {
          color: #ffffff;
          font-size: 36px;
          font-weight: 600;
          margin-bottom: 16px;
          line-height: 1.2;
          font-family: var(--font-family-Geist);
        }

       .feature-main-container .card p {
          color: rgba(255, 255, 255, 0.75);
          font-size: 24px;
          line-height: 1.2;
          margin: 0;
          font-family: var(--font-family-Satoshi);
        }

        @media (max-width: 768px) {
          .feature-main-container .card {
            width: 330px;
            height:336px;
            padding: 32px 24px;
          }
         .feature-main-container .card h3 {
            font-size: 28px;
          }
          .feature-main-container .card p {
            font-size: 22px;
          }
        .cards-track{
            padding: 0 22px;}
        }
      `}</style>

      <div className="feature-main-container">
        <div className="slider-section">
          {/* Scroll Progress Indicator */}
          {/* Cards Slider */}
          <motion.div
            className="cards-wrapper"
            ref={sliderRef}
            style={{ maskImage }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div className="cards-track">
              {cards.map((card, index) => (
                <div key={index} className="card">
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

// === Scroll Mask Hook (from your animation) ===
const left = `0%`;
const right = `100%`;
const leftInset = `20%`;
const rightInset = `80%`;
const transparent = "#0000";
const opaque = "#000";

export function useScrollOverflowMask(scrollXProgress) {
  const maskImage = useMotionValue(
    `linear-gradient(90deg, ${opaque}, ${opaque} ${left}, ${opaque} ${rightInset}, ${transparent})`
  );

  useMotionValueEvent(scrollXProgress, "change", (value) => {
    if (value === 0) {
      animate(
        maskImage,
        `linear-gradient(90deg, ${opaque}, ${opaque} ${left}, ${opaque} ${rightInset}, ${transparent})`
      );
    } else if (value === 1) {
      animate(
        maskImage,
        `linear-gradient(90deg, ${transparent}, ${opaque} ${leftInset}, ${opaque} ${right}, ${opaque})`
      );
    } else if (
      scrollXProgress.getPrevious() === 0 ||
      scrollXProgress.getPrevious() === 1
    ) {
      animate(
        maskImage,
        `linear-gradient(90deg, ${transparent}, ${opaque} ${leftInset}, ${opaque} ${rightInset}, ${transparent})`
      );
    }
  });

  return maskImage;
}

export default FeatureHighlightMotion;
